import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useCurrentUser } from './use-current-user';
import { useCurrentOrganization } from './use-current-organization';

interface SubscriptionQueryOptions {
  enabled?: boolean;
}

export interface SubscriptionPlan {
  id: string;
  plan_name: string;
  max_projects_per_seat?: number | null;
  max_pdf_plans_per_seat?: number | null;
  seat_count?: number;
  price_per_seat_monthly?: number;
  price_per_seat_yearly?: number;
  apple_product_id_monthly?: string | null;
  apple_product_id_quarterly?: string | null;
  google_product_id_monthly?: string | null;
  google_product_id_quarterly?: string | null;
  billing_interval?: 'monthly' | 'quarterly' | 'yearly';
  min_seats?: number;
  max_seats?: number | null;
  currency: string;
  features: string[];
  status: string;
  provider?: 'stripe' | 'apple' | 'google' | null;
  provider_environment?: 'production' | 'sandbox' | null;
  provider_product_id?: string | null;
  last_verified_at?: string | null;
  current_period_end?: string;
  cancel_at_period_end: boolean;
}

export interface SubscriptionLimits {
  maxProjects: number;
  currentProjects: number;
  maxPdfPlans: number;
  currentPdfPlans: number;
  maxTeamMembers: number;
  currentTeamMembers: number;
  maxActiveTickets: number;
  currentActiveTickets: number;
  status: string;
  planName: string;
  features: string[];
}

export function useOrganizationSubscription(options: SubscriptionQueryOptions = {}) {
  const { user } = useCurrentUser();
  const { organizationId, isContractor } = useCurrentOrganization();
  // Use selected organization ID if available, otherwise fall back to user's organization
  const activeOrgId = organizationId || user?.organizationId;
  const isEnabledByCaller = options.enabled ?? true;

  return useQuery({
    queryKey: ['/api/subscriptions/organization', activeOrgId],
    queryFn: async (): Promise<SubscriptionPlan | null> => {
      if (!activeOrgId) return null;
      
      const response = await apiRequest(`/api/subscriptions/organization/${activeOrgId}`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch subscription');
      }
      
      return data.subscription;
    },
    enabled: !!activeOrgId && !isContractor && isEnabledByCaller, // Disable for contractors
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useSubscriptionLimits(options: SubscriptionQueryOptions = {}) {
  const { user } = useCurrentUser();
  const { organizationId, isContractor } = useCurrentOrganization();
  // Use selected organization ID if available, otherwise fall back to user's organization
  const activeOrgId = organizationId || user?.organizationId;
  const isEnabledByCaller = options.enabled ?? true;

  return useQuery({
    queryKey: ['/api/subscriptions/organization', activeOrgId, 'usage'],
    queryFn: async (): Promise<SubscriptionLimits | null> => {
      if (!activeOrgId) return null;
      
      const response = await apiRequest(`/api/subscriptions/organization/${activeOrgId}/usage`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch subscription limits');
      }
      
      return data.usage;
    },
    enabled: !!activeOrgId && !isContractor && isEnabledByCaller, // Disable for contractors
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useSubscriptionPlans(options: SubscriptionQueryOptions = {}) {
  const isEnabledByCaller = options.enabled ?? true;
  return useQuery({
    queryKey: ['/api/subscriptions/plans'],
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      const response = await apiRequest('/api/subscriptions/plans');
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch subscription plans');
      }
      
      return data.plans;
    },
    enabled: isEnabledByCaller,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useCanAccessFeature(feature: string): boolean {
  const { data: subscription } = useOrganizationSubscription();
  
  if (!subscription?.features) return false;
  
  return subscription.features.includes(feature);
}
